import { Loader2, Target, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';

function formatTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hr = parseInt(h, 10);
  const suffix = hr >= 12 ? 'PM' : 'AM';
  const displayHr = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
  return `${displayHr}:${m} ${suffix}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function groupVisitsByDate(visits) {
  const groups = {};
  (visits || []).forEach(v => {
    if (!groups[v.appointment_date]) groups[v.appointment_date] = [];
    groups[v.appointment_date].push(v);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

const CATEGORY_CONFIG = {
  pointed:     { label: 'Pointed',    variant: 'success' },
  zero_points: { label: '0 Points',   variant: 'warning' },
  cancelled:   { label: 'Cancelled',  variant: 'default' },
  no_show:     { label: 'No Show',    variant: 'error' },
  missed:      { label: 'Missed',     variant: 'error' },
};

function PointBreakdown({ visit }) {
  if (visit.category !== 'pointed' && visit.category !== 'zero_points') return null;

  if (visit.point_source === 'outpatient' && visit.cpt_codes?.length > 0) {
    const parts = visit.cpt_codes.map(c => `${c.code} (${c.minutes}m)`);
    return (
      <span className="text-xs text-gray-500">
        {parts.join(' + ')} = {visit.total_minutes}m {visit.points > 0 ? `→ ${visit.points} pts` : ''}
      </span>
    );
  }

  if (visit.point_source === 'home_health') {
    return (
      <span className="text-xs text-gray-500">
        {visit.actual_duration_minutes}m actual {visit.points > 0 ? `→ ${visit.points} pts` : ''}
      </span>
    );
  }

  if (visit.zero_reason) {
    return <span className="text-xs text-gray-400 italic">{visit.zero_reason}</span>;
  }

  return null;
}

function SummaryBar({ summary }) {
  if (!summary) return null;

  const stats = [
    { label: 'Total Points', value: summary.total_points, color: 'text-gray-900' },
    { label: 'Visit Points', value: summary.total_visit_points, color: 'text-green-700' },
    { label: 'Admin Points', value: summary.total_admin_points, color: 'text-blue-700' },
    { label: 'Pointed Visits', value: summary.pointed_visit_count, color: 'text-green-700' },
    { label: 'Zero-Point', value: summary.zero_points_count, color: 'text-yellow-700' },
    { label: 'Missed', value: summary.missed_count, color: 'text-red-700' },
    { label: 'Cancelled', value: summary.cancelled_count, color: 'text-gray-500' },
    { label: 'No-Show', value: summary.no_show_count, color: 'text-red-700' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      {stats.map(s => (
        <div key={s.label} className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-xs text-gray-500">{s.label}</p>
          <p className={cn('text-lg font-semibold', s.color)}>
            {typeof s.value === 'number' ? (Number.isInteger(s.value) ? s.value : s.value.toFixed(2)) : 0}
          </p>
        </div>
      ))}
    </div>
  );
}

function VisitRow({ visit, runningTotal, onVisitClick }) {
  const config = CATEGORY_CONFIG[visit.category] || CATEGORY_CONFIG.missed;
  const isClickable = !!visit.encounter_id;

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 last:border-b-0',
        isClickable && 'cursor-pointer hover:bg-gray-50 group'
      )}
      onClick={isClickable ? () => onVisitClick(visit.encounter_id) : undefined}
    >
      <div className="w-16 flex-shrink-0 text-xs text-gray-500">
        {formatTime(visit.start_time)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {visit.patient_name}
          </span>
          <Badge variant={config.variant}>{config.label}</Badge>
          {visit.points > 0 && (
            <span className="text-sm font-semibold text-green-700">{visit.points} pts</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {visit.appointment_type && (
            <span className="text-xs text-gray-500">{visit.appointment_type}</span>
          )}
          <PointBreakdown visit={visit} />
          {visit.category === 'missed' && (
            <span className="text-xs text-gray-400 italic">
              Status: {visit.status}
            </span>
          )}
        </div>
      </div>
      <div className="text-sm font-mono text-gray-400 flex-shrink-0 w-12 text-right">
        {runningTotal}
      </div>
      {isClickable && (
        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
      )}
    </div>
  );
}

function AdminBlocksSection({ blocks, runningTotals }) {
  if (!blocks?.length) return null;

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" />
        Admin Time
      </h4>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {blocks.map((block, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 last:border-b-0">
            <div className="w-24 flex-shrink-0 text-xs text-gray-500">
              {formatDate(block.date)}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-700">
                {formatTime(block.start_time)} - {formatTime(block.end_time)}
              </span>
              {block.reason && (
                <span className="text-xs text-gray-500 ml-2">{block.reason}</span>
              )}
            </div>
            <div className="text-xs text-gray-500">{block.duration_minutes}m</div>
            <div className="text-sm font-semibold text-blue-700">{block.points} pts</div>
            <div className="text-sm font-mono text-gray-400 flex-shrink-0 w-12 text-right">
              {runningTotals[i]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProviderPointDetail({ data, loading, onVisitClick }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading point detail...
      </div>
    );
  }

  if (!data) return null;

  const { visits, admin_blocks, summary } = data;
  const groupedVisits = groupVisitsByDate(visits);
  const hasVisits = visits?.length > 0;
  const hasAdmin = admin_blocks?.length > 0;

  let runningTotal = 0;
  const visitRunningTotals = (visits || []).map(v => {
    runningTotal += v.points;
    return runningTotal;
  });
  const adminRunningTotals = (admin_blocks || []).map(b => {
    runningTotal += b.points;
    return runningTotal;
  });

  if (!hasVisits && !hasAdmin) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <AlertCircle className="h-4 w-4" />
        No visit data for this period.
      </div>
    );
  }

  return (
    <div>
      <SummaryBar summary={summary} />

      {hasVisits && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {(() => {
            let visitIndex = 0;
            return groupedVisits.map(([date, dateVisits]) => (
              <div key={date}>
                <div className="bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 sticky top-0 flex items-center justify-between">
                  <span>{formatDate(date)}</span>
                  {visitIndex === 0 && (
                    <span className="text-gray-400 font-normal">Running Total</span>
                  )}
                </div>
                {dateVisits.map((visit, i) => {
                  const rt = visitRunningTotals[visitIndex++];
                  return (
                    <VisitRow key={visit.appointment_id || i} visit={visit} runningTotal={rt} onVisitClick={onVisitClick} />
                  );
                })}
              </div>
            ));
          })()}
        </div>
      )}

      <AdminBlocksSection blocks={admin_blocks} runningTotals={adminRunningTotals} />
    </div>
  );
}
